import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';

import callAll from '@dndbeyond/ddb-common-components/dist/Utils/Input/callAll';
import useApiCall from '@dndbeyond/ddb-common-components/dist/Utils/useApiCall';

import CharacterServiceApi from '../../api/CharacterServiceApi';
import analytics from '../analytics';
import { CharacterData, CharacterStatusEnum } from '../typings';
import CharacterUtils from '../utils/CharacterUtils';
import ApiStatusIndicator from './ApiStatusIndicator';
import './CharacterCard.scss';
import ConfirmationModal from './MUIConfirmationModal';
import { UnlockConfirmationMessage } from './MyCharactersListing';

interface CopyProps {
    character: CharacterData;
    copyCharacter: () => void;
}
const CopyButton: React.FC<CopyProps> = ({ character, copyCharacter }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div>
            <ConfirmationModal
                onClose={() => {
                    setIsOpen(false);
                    analytics.logCharacterCopyCancelled();
                }}
                isOpen={isOpen}
                onConfirm={() => {
                    copyCharacter();
                    analytics.logCharacterCopyConfirmed();
                    setIsOpen(false);
                }}
                message={`Are you sure you want to copy ${CharacterUtils.getName(character)}?`}
                title="Copy this character?"
                confirmText="Copy"
            />
            <Button
                size="small"
                variant="text"
                onClick={() => {
                    analytics.logCharacterCopyClicked();
                    setIsOpen(true);
                }}
            >
                Copy
            </Button>
        </div>
    );
};

interface DeleteProps {
    character: CharacterData;
    deleteCharacter: () => void;
}
const DeleteButton: React.FC<DeleteProps> = ({ character, deleteCharacter }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div>
            <ConfirmationModal
                isOpen={isOpen}
                onClose={() => {
                    analytics.logCharacterDeleteCancelled();
                    setIsOpen(false);
                }}
                onConfirm={() => {
                    deleteCharacter();
                    analytics.logCharacterDeleteConfirmed();
                    setIsOpen(false);
                }}
                message={`To delete ${CharacterUtils.getName(character)}, type the word DELETE into the field below.`}
                title="Delete this character?"
                typeValueToConfirm="delete"
                confirmText="Delete"
            />
            <Button
                size="small"
                variant="text"
                onClick={() => {
                    analytics.logCharacterDeleteClicked();
                    setIsOpen(true);
                }}
                color="error"
            >
                Delete
            </Button>
        </div>
    );
};

interface ActivateButtonProps {
    character: CharacterData;
    activateCharacter: (character: CharacterData) => void;
    maxCharacterSlotsAllowed: number | null;
}
const ActivateButton: React.FC<ActivateButtonProps> = ({ character, activateCharacter, maxCharacterSlotsAllowed }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div>
            <ConfirmationModal
                isOpen={isOpen}
                onClose={() => {
                    setIsOpen(false);
                }}
                onConfirm={() => {
                    activateCharacter(character);
                    analytics.logUnlockCharacterUnlocked();
                    setIsOpen(false);
                }}
                title="Activate these characters?"
                confirmText="Activate"
                message={
                    <UnlockConfirmationMessage
                        charactersToUnlock={[character]}
                        maxCharacterSlotsAllowed={maxCharacterSlotsAllowed}
                    />
                }
            />
            <Button size="small" variant="text" onClick={() => setIsOpen(true)}>
                Activate
            </Button>
        </div>
    );
};

interface LeaveCampaignProps {
    character: CharacterData;
    leaveCampaign: () => void;
}
const LeaveCampaignButton: React.FC<LeaveCampaignProps> = ({ character, leaveCampaign }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div>
            <ConfirmationModal
                isOpen={isOpen}
                onClose={() => {
                    analytics.logCharacterLeaveCampaignCancelled();
                    setIsOpen(false);
                }}
                onConfirm={() => {
                    leaveCampaign();
                    analytics.logCharacterLeaveCampaignConfirmed();
                    setIsOpen(false);
                }}
                message={`Are you sure you want ${CharacterUtils.getName(
                    character,
                )} to leave ${CharacterUtils.getCampaignName(character)}?`}
                title="Leave this Campaign?"
                confirmText="Leave"
            />
            <Button
                size="small"
                variant="text"
                onClick={() => {
                    analytics.logCharacterLeaveCampaignClicked();
                    setIsOpen(true);
                }}
                // color='error'
                sx={{
                    color: '#d24040',
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                }}
            >
                Leave Campaign
            </Button>
        </div>
    );
};

interface CharacterCardLinksProps extends Omit<CharacterCardProps, 'reloadListing'> {
    copyCharacter: () => void;
    deleteCharacter: () => void;
    activateCharacter: () => void;
}
const CharacterCardLinks: React.FC<CharacterCardLinksProps> = ({
    character,
    hasLockedCharacters,
    overCharacterLimit,
    copyCharacter,
    deleteCharacter,
    lockCharacter,
    unlockCharacter,
    canUnlockMore,
    maxCharacterSlotsAllowed,
    activateCharacter,
    currentCharacterCount,
}) => {
    if (hasLockedCharacters && CharacterUtils.getStatus(character) === CharacterStatusEnum.Active) {
        return (
            <>
                <Button
                    onClick={callAll(
                        () => lockCharacter(character),
                        () => analytics.logUnlockCharacterLocked(),
                    )}
                    variant="contained"
                    size="small"
                >
                    Deselect
                </Button>
                <Typography color="primary" noWrap>
                    This Character will be activated.
                </Typography>
            </>
        );
    } else if (hasLockedCharacters && CharacterUtils.getStatus(character) === CharacterStatusEnum.Locked) {
        return (
            <>
                <Button
                    onClick={callAll(
                        () => unlockCharacter(character),
                        () => analytics.logUnlockCharacterUnlocked(),
                    )}
                    variant="outlined"
                    size="small"
                    disabled={!canUnlockMore}
                    title={
                        !canUnlockMore
                            ? 'Max characters selected to unlock. Lock other characters to unlock this character.'
                            : undefined
                    }
                >
                    Select
                </Button>
                <Typography noWrap>This Character will be deactivated.</Typography>
            </>
        );
    } else if (!hasLockedCharacters && CharacterUtils.getStatus(character) === CharacterStatusEnum.Locked) {
        return (
            <React.Fragment>
                {(maxCharacterSlotsAllowed === null || currentCharacterCount < maxCharacterSlotsAllowed) && (
                    <ActivateButton
                        character={character}
                        activateCharacter={activateCharacter}
                        maxCharacterSlotsAllowed={maxCharacterSlotsAllowed}
                    />
                )}
                <DeleteButton character={character} deleteCharacter={deleteCharacter} />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Button
                href={CharacterUtils.getDetailsLink(character)}
                onClick={() => analytics.logCharacterViewClicked()}
                component={Link}
                size="small"
                variant="text"
                sx={{
                    '&:visited': {
                        color: 'primary.main',
                    },
                    '&:hover': {
                        color: 'primary.main',
                    },
                }}
            >
                View
            </Button>
            <Button
                href={CharacterUtils.getEditLink(character)}
                onClick={() => analytics.logCharacterEditClicked()}
                component={Link}
                size="small"
                variant="text"
                sx={{
                    '&:visited': {
                        color: 'primary.main',
                    },
                    '&:hover': {
                        color: 'primary.main',
                    },
                }}
            >
                Edit
            </Button>
            {!overCharacterLimit && <CopyButton character={character} copyCharacter={copyCharacter} />}
            <DeleteButton character={character} deleteCharacter={deleteCharacter} />
        </React.Fragment>
    );
};

export interface CharacterCardProps {
    character: CharacterData;
    reloadListing: () => void;
    lockCharacter: (character: CharacterData) => void;
    unlockCharacter: (character: CharacterData) => void;
    canUnlockMore: boolean;
    hasLockedCharacters: boolean;
    overCharacterLimit: boolean;
    maxCharacterSlotsAllowed: number | null;
    currentCharacterCount: number;
}
const CharacterCard: React.FC<CharacterCardProps> = ({
    character,
    reloadListing,
    lockCharacter,
    unlockCharacter,
    canUnlockMore,
    hasLockedCharacters,
    overCharacterLimit,
    maxCharacterSlotsAllowed,
    currentCharacterCount,
}) => {
    // TODO type this stuff?
    const [copyCharacter, isCopyingCharacter, errorCopyingCharacter] = useApiCall(
        CharacterServiceApi.copyCharacter,
        reloadListing,
    );

    const [deleteCharacter, isDeletingCharacter, errorDeletingCharacter] = useApiCall(
        CharacterServiceApi.deleteCharacter,
        reloadListing,
    );

    const [leaveCampaign, isLeavingCampaign, errorLeavingCampaign] = useApiCall(
        CharacterServiceApi.leaveCampaign,
        reloadListing,
    );

    const [activateCharacter, isActivatingCharacter, errorActivatingCharacter] = useApiCall(
        CharacterServiceApi.activateCharacter,
        reloadListing,
    );

    return (
        <li className="ddb-campaigns-character-card-wrapper j-characters-listing__item">
            <Box
                className={`status-${CharacterUtils.getStatusSlug(character)}`}
                sx={{
                    border: '1px solid #d1cdca',
                    background: '#fff',
                    position: 'relative',
                }}
            >
                <div className="ddb-campaigns-character-card-header">
                    {CharacterUtils.getBackdropUrl(character) ? (
                        <div
                            className="ddb-campaigns-character-card-header-cover-image ddb-campaigns-character-card-header-cover-image-user-backdrop"
                            style={{
                                backgroundImage: `url(${CharacterUtils.getBackdropUrl(character)})`,
                            }}
                        />
                    ) : (
                        <div
                            className="ddb-campaigns-character-card-header-cover-image"
                            style={{
                                backgroundImage: `url(${CharacterUtils.getCoverImageUrl(character)})`,
                            }}
                        />
                    )}
                    <div className="ddb-campaigns-character-card-header-upper">
                        {!hasLockedCharacters && CharacterUtils.getStatus(character) === CharacterStatusEnum.Active && (
                            <a
                                href={CharacterUtils.getDetailsLink(character)}
                                className="ddb-campaigns-character-card-header-upper-details-link"
                                // There already is a view link - this is just making the top area clickable so it shouldn't be tabbed to or announced
                                tabIndex={-1}
                                aria-hidden={true}
                            />
                        )}
                        <div className="ddb-campaigns-character-card-header-upper-portrait">
                            {CharacterUtils.getAvatarUrl(character) ? (
                                <div
                                    className="image user-selected-avatar"
                                    style={{
                                        backgroundImage: `url(${CharacterUtils.getAvatarUrl(character)})`,
                                    }}
                                />
                            ) : (
                                <div className="image default-character-avatar" />
                            )}
                        </div>
                        <div className="ddb-campaigns-character-card-header-upper-character-info">
                            <div className="ddb-campaigns-character-card-header-upper-character-info-primary">
                                {CharacterUtils.getName(character)}
                            </div>
                            <div className="ddb-campaigns-character-card-header-upper-character-info-secondary">
                                {CharacterUtils.getSecondaryInfo(character)}
                            </div>
                        </div>
                    </div>
                </div>
                {hasLockedCharacters && CharacterUtils.getStatus(character) === CharacterStatusEnum.Active && (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            position: 'absolute',
                            top: '-10px',
                            right: '-10px',
                            zIndex: '1',
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                        }}
                    >
                        <Button
                            aria-label="click to lock"
                            variant="contained"
                            startIcon={<CheckIcon sx={{ '& path': { fill: '#fff' } }} />}
                            color="primary"
                            onClick={() => {
                                lockCharacter(character);
                                analytics.logUnlockCharacterLocked();
                            }}
                            sx={{
                                minWidth: '50px',
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                '& span': { margin: 0 },
                            }}
                        />
                    </Stack>
                )}
                {hasLockedCharacters && CharacterUtils.getStatus(character) === CharacterStatusEnum.Locked && (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            position: 'absolute',
                            top: '-10px',
                            right: '-10px',
                            zIndex: '1',
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            bgcolor: '#DCDFE1',
                        }}
                    >
                        <Button
                            aria-label="click to unlock"
                            variant="contained"
                            startIcon={
                                <LockOutlinedIcon
                                    sx={{
                                        '& path': { fill: '#525C63' },
                                    }}
                                />
                            }
                            disabled={!canUnlockMore}
                            onClick={() => {
                                unlockCharacter(character);
                                analytics.logUnlockCharacterUnlocked();
                            }}
                            // TODO color: theme => theme.palette.grey[500],
                            sx={{
                                minWidth: '50px',
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                bgcolor: '#DCDFE1',
                                '& span': { margin: 0 },
                                '&.Mui-disabled': { bgcolor: '#DCDFE1' },
                                '&:hover': { bgcolor: '#99a2a8' },
                            }}
                        />
                    </Stack>
                )}
                {CharacterUtils.isInCampaign(character) && (
                    <div className="ddb-campaigns-character-card-campaign-links">
                        <div className="ddb-campaigns-character-card-campaign-links-campaign">
                            <strong>Campaign:</strong>{' '}
                            {!hasLockedCharacters ? (
                                <a
                                    className="ddb-campaigns-character-card-campaign-links-campaign-link"
                                    href={CharacterUtils.getCampaignDetailsLink(character)}
                                    onClick={() => analytics.logCharacterCampaignClicked()}
                                >
                                    {CharacterUtils.getCampaignName(character)}
                                </a>
                            ) : (
                                <span>{CharacterUtils.getCampaignName(character)}</span>
                            )}
                            {!CharacterUtils.getIsAssigned(character) && ' (Unassigned)'}
                        </div>
                        <div className="ddb-campaigns-character-card-campaign-links-actions">
                            {!hasLockedCharacters && (
                                <LeaveCampaignButton
                                    character={character}
                                    leaveCampaign={() => leaveCampaign(character)}
                                />
                            )}
                        </div>
                    </div>
                )}
                <div className="ddb-campaigns-character-card-footer">
                    <div
                        className={['ddb-campaigns-character-card-footer-links', hasLockedCharacters && 'is-unlocking']
                            .filter(Boolean)
                            .join(' ')}
                    >
                        <CharacterCardLinks
                            character={character}
                            hasLockedCharacters={hasLockedCharacters}
                            overCharacterLimit={overCharacterLimit}
                            copyCharacter={() => copyCharacter(character)}
                            deleteCharacter={() => deleteCharacter(character)}
                            lockCharacter={lockCharacter}
                            unlockCharacter={unlockCharacter}
                            canUnlockMore={canUnlockMore}
                            maxCharacterSlotsAllowed={maxCharacterSlotsAllowed}
                            activateCharacter={() => activateCharacter(character)}
                            currentCharacterCount={currentCharacterCount}
                        />
                    </div>
                    <ApiStatusIndicator
                        isLoading={isCopyingCharacter}
                        error={errorCopyingCharacter}
                        loadingMessage={`Copying ${CharacterUtils.getName(character)}`}
                        errorMessage={`Error copying ${CharacterUtils.getName(character)}`}
                    />
                    <ApiStatusIndicator
                        isLoading={isDeletingCharacter}
                        error={errorDeletingCharacter}
                        loadingMessage={`Deleting ${CharacterUtils.getName(character)}`}
                        errorMessage={`Error deleting ${CharacterUtils.getName(character)}`}
                    />
                    <ApiStatusIndicator
                        isLoading={isActivatingCharacter}
                        error={errorActivatingCharacter}
                        loadingMessage={`Activating ${CharacterUtils.getName(character)}`}
                        errorMessage={`Error activating ${CharacterUtils.getName(character)}`}
                    />
                    <ApiStatusIndicator
                        isLoading={isLeavingCampaign}
                        error={errorLeavingCampaign}
                        loadingMessage={`Leaving ${CharacterUtils.getCampaignName(character)}`}
                        errorMessage={`Error leaving ${CharacterUtils.getCampaignName(character)}`}
                    />
                </div>
            </Box>
        </li>
    );
};

export default CharacterCard;
